import { sql } from 'drizzle-orm';
import { recurringTransactions, transactions } from '@/db/schema';
import { AdjustmentType, PERSONAL_LOAN_PRINCIPAL_ENTRY_TYPES } from '@/lib/constants';

// Predicados canônicos do Lançamento analítico (ver CONTEXT.md): o que conta como
// receita/despesa numa leitura financeira. Ficam fora os artefatos de fatura
// (contabilidade interna do cartão) e o principal de Empréstimo a pessoa (troca de
// ativo); leituras de caixa/saldo tiram também a linha crua de compra no cartão.
// Não re-inlinar estas listas em queries — importe daqui.

export const NON_ANALYTIC_INVOICE_ADJUSTMENT_TYPES = [
    AdjustmentType.INVOICE_CREDIT,
    AdjustmentType.INVOICE_EXPENSE,
    AdjustmentType.INVOICE_MANUAL_ADJUSTMENT,
    AdjustmentType.INVOICE_MANUAL_ENTRY,
    AdjustmentType.INVOICE_PAYMENT,
    AdjustmentType.INVOICE_REMAINDER,
] as const;

export const INVOICE_TOTAL_EXCLUDED_ADJUSTMENT_TYPES = [
    AdjustmentType.INVOICE_EXPENSE,
    AdjustmentType.INVOICE_PAYMENT,
] as const;

function excludeAdjustmentTypes(adjustmentTypes: readonly string[]) {
    return sql`(${transactions.adjustmentType} IS NULL OR ${transactions.adjustmentType} NOT IN (${sql.join(
        adjustmentTypes.map((adjustmentType) => sql`${adjustmentType}`),
        sql`, `,
    )}))`;
}

export function excludeNonAnalyticInvoiceAdjustments() {
    return excludeAdjustmentTypes(NON_ANALYTIC_INVOICE_ADJUSTMENT_TYPES);
}

export function excludeInvoiceSettlementAdjustments() {
    return excludeAdjustmentTypes(INVOICE_TOTAL_EXCLUDED_ADJUSTMENT_TYPES);
}

export function excludeRawCreditCardTransactions() {
    return sql`(${transactions.creditCardId} IS NULL OR ${transactions.adjustmentType} IS NOT NULL)`;
}

export function isNonAnalyticInvoiceAdjustment(adjustmentType: string | null | undefined): boolean {
    if (!adjustmentType) return false;
    return NON_ANALYTIC_INVOICE_ADJUSTMENT_TYPES.includes(
        adjustmentType as (typeof NON_ANALYTIC_INVOICE_ADJUSTMENT_TYPES)[number],
    );
}

// Empréstimo a pessoa é troca de ativo: a saída e o recebimento movem caixa mas
// não são despesa/receita — ficam fora dos relatórios e análises por categoria.
// Excedente e baixa NÃO entram aqui: são receita/despesa de verdade.
export function excludePersonalLoanPrincipal() {
    return sql`(${transactions.personalLoanEntryType} IS NULL OR ${transactions.personalLoanEntryType} NOT IN (${sql.join(
        PERSONAL_LOAN_PRINCIPAL_ENTRY_TYPES.map((entryType) => sql`${entryType}`),
        sql`, `,
    )}))`;
}

export function isPersonalLoanPrincipalEntry(personalLoanEntryType: string | null | undefined): boolean {
    if (!personalLoanEntryType) return false;
    return PERSONAL_LOAN_PRINCIPAL_ENTRY_TYPES.includes(
        personalLoanEntryType as (typeof PERSONAL_LOAN_PRINCIPAL_ENTRY_TYPES)[number],
    );
}

// Parcelamento pelo valor total (preferência, ADR 0043): em leituras ANALÍTICAS
// em competência, a compra parcelada conta UMA vez, pelo valor total, na
// parcela-âncora (a MENOR parcela existente do grupo — nem sempre a nº 1:
// "a partir de 3/5" cadastra só da 3 em diante). As demais parcelas saem do SUM.
// Fatura, extrato, saldo e caixa NÃO usam estes predicados — lá cada parcela é
// dinheiro real no seu mês. Espelho JS: collapseInstallmentRows em lib/installments.ts.

/**
 * Recorte analítico da parcela IRMÃ, espelhando o WHERE de fora (ADR 0077).
 *
 * As subqueries de âncora e de soma rodam sobre `transactions` cru, então precisam repetir
 * as exclusões que a consulta externa aplica — senão desfazem invariantes de outros ADRs em
 * silêncio: âncora numa conta arquivada (ADR 0061) era removida pelo WHERE de fora enquanto
 * as demais parcelas eram removidas por não serem a âncora, e o grupo INTEIRO evaporava da
 * leitura; e parcela num cartão excluído (ADR 0068) continuava entrando no SUM, ressuscitando
 * dinheiro que o app prometeu ter tirado.
 */
function installmentSiblingScope(alias: 'anchor' | 'sibling') {
    const row = sql.raw(alias);
    return sql`${row}.deleted_at IS NULL
          AND (${row}.is_ignored = 0 OR ${row}.is_ignored IS NULL)
          AND (${row}.account_id IS NULL OR NOT EXISTS (
            SELECT 1 FROM accounts hidden_sibling_account
            WHERE hidden_sibling_account.id = ${row}.account_id
              AND (hidden_sibling_account.is_archived = 1 OR hidden_sibling_account.is_excluded_from_totals = 1)
          ))
          AND (
            (${row}.is_confirmed = 1 AND ${row}.account_id IS NOT NULL)
            OR NOT EXISTS (
              SELECT 1 FROM credit_cards dead_sibling_card
              WHERE dead_sibling_card.deleted_at IS NOT NULL
                AND dead_sibling_card.id = COALESCE(
                  ${row}.credit_card_id,
                  (SELECT owner_invoice.credit_card_id FROM invoices owner_invoice
                    WHERE owner_invoice.id = ${row}.invoice_id)
                )
            )
          )`;
}

/** Mantém só linhas fora de parcelamento ou a parcela-âncora do grupo. */
export function onlyInstallmentAnchor() {
    return sql`(${transactions.installmentId} IS NULL OR ${transactions.installmentNumber} = (
        SELECT MIN(anchor.installment_number) FROM transactions anchor
        WHERE anchor.installment_id = ${transactions.installmentId}
          AND ${installmentSiblingScope('anchor')}
    ))`;
}

/**
 * Valor analítico da linha com Parcelamento pelo valor total: parcela vira a
 * soma do grupo inteiro (vivas), linha comum segue com o próprio amount.
 * Usar SEMPRE em conjunto com onlyInstallmentAnchor() — sem o predicado, cada
 * parcela somaria o grupo de novo.
 */
export function installmentFullValueAmount() {
    return sql`(CASE WHEN ${transactions.installmentId} IS NULL THEN ${transactions.amount} ELSE (
        SELECT COALESCE(SUM(sibling.amount), 0) FROM transactions sibling
        WHERE sibling.installment_id = ${transactions.installmentId}
          AND ${installmentSiblingScope('sibling')}
    ) END)`;
}

// Conta oculta fora da leitura em COMPETÊNCIA (ADR 0061): conta ARQUIVADA ou "FORA DO
// SALDO TOTAL" já não soma em nenhum saldo (buildAccountConditions, forecast) — os fluxos
// dela também ficam fora do Balanço do mês, pizzas e drill-downs em competência, senão o
// Resultado do mês infla com dinheiro que não aparece em saldo nenhum. Compra de cartão
// (account_id NULL) segue dentro: o gasto pertence ao cartão, não à conta pagadora.
// Extrato, saldo/detalhe de conta, fatura, caixa (cash-flow) e relatórios com filtro
// explícito de conta NÃO usam isto. Subquery correlata de propósito: o predicado funciona
// em qualquer query, com ou sem join de accounts.

/** Transações de conta arquivada/fora do total saem do SUM analítico em competência. */
export function excludeHiddenAccountTransactions() {
    return sql`(${transactions.accountId} IS NULL OR NOT EXISTS (
        SELECT 1 FROM accounts hidden_account
        WHERE hidden_account.id = ${transactions.accountId}
          AND (hidden_account.is_archived = 1 OR hidden_account.is_excluded_from_totals = 1)
    ))`;
}

/** Mesmo recorte para REGRAS de recorrência (projeção analítica de meses futuros). */
export function excludeHiddenAccountRecurringRules() {
    return sql`(${recurringTransactions.accountId} IS NULL OR NOT EXISTS (
        SELECT 1 FROM accounts hidden_account
        WHERE hidden_account.id = ${recurringTransactions.accountId}
          AND (hidden_account.is_archived = 1 OR hidden_account.is_excluded_from_totals = 1)
    ))`;
}

// Cartão excluído some do app inteiro (ADR 0068): Home e forecast já iteram só cartões
// vivos; sem este predicado o extrato/mês seguia mostrando o lado do cartão morto — era a
// fatura zumbi congelada do caso Matheus. A exceção deliberada é a linha CONFIRMADA com
// conta (pagamento de fatura pago): ela debitou saldo, e escondê-la faria extrato e saldo
// da conta não fecharem. O cartão é resolvido via linha OU via fatura (a despesa prevista
// antiga pode não carregar credit_card_id). Subquery correlata de propósito, como
// excludeHiddenAccountTransactions: funciona em qualquer query, com ou sem join.
export function excludeDeletedCardTransactions() {
    return sql`(
        (${transactions.isConfirmed} = 1 AND ${transactions.accountId} IS NOT NULL)
        OR NOT EXISTS (
            SELECT 1 FROM credit_cards dead_card
            WHERE dead_card.deleted_at IS NOT NULL
              AND dead_card.id = COALESCE(
                ${transactions.creditCardId},
                (SELECT owner_invoice.credit_card_id FROM invoices owner_invoice
                  WHERE owner_invoice.id = ${transactions.invoiceId})
              )
        )
    )`;
}

/**
 * A **Origem** de um Lançamento do lado do cartão (ADR 0103): o cartão em que ele acontece.
 *
 * Não basta ler `credit_card_id`. Artefatos de fatura e linhas de reconciliação carregam
 * só o `invoice_id`, e ler a coluna crua perderia esse gasto **em silêncio** — o mesmo
 * COALESCE que `excludeDeletedCardTransactions` já usa logo acima. Quem filtrar ou agrupar
 * por cartão importa daqui em vez de reescrever a expressão.
 *
 * O lado da conta é `transactions.account_id` direto: Conta e Cartão são excludentes no
 * lançamento (o formulário é um XOR), então uma linha tem no máximo uma Origem.
 */
export function originCardIdExpr() {
    return sql`COALESCE(
        ${transactions.creditCardId},
        (SELECT owner_invoice.credit_card_id FROM invoices owner_invoice
          WHERE owner_invoice.id = ${transactions.invoiceId})
    )`;
}

// Lançamento ignorado (ADR 0037): saiu de TODA leitura analítica por vontade do usuário
// (Balanço do mês, relatórios, gráficos, orçamento, resumo da IA, em competência E caixa,
// e o Saldo do período). Aplicar SÓ nos SUMs analíticos — NUNCA no recálculo de saldo da
// conta nem no total da fatura (o dinheiro segue real ali). O guard IS NULL é defensivo
// (linha montada à mão sem a coluna). Ver CONTEXT.md § Lançamento ignorado.
export function excludeIgnored() {
    return sql`(${transactions.isIgnored} = 0 OR ${transactions.isIgnored} IS NULL)`;
}

/**
 * Mesmo recorte para REGRAS de recorrência (ADR 0169): a Ocorrência projetada herda o
 * Ignorado da regra, então a leitura analítica que projeta a regra a tira na origem. Quem
 * projeta para SALDO (previsto da conta, curto/médio prazo) não usa isto — o dinheiro é real.
 */
export function excludeIgnoredRecurringRules() {
    return sql`(${recurringTransactions.isIgnored} = 0 OR ${recurringTransactions.isIgnored} IS NULL)`;
}

/**
 * Espelho JS do predicado — para filtrar em memória (pipeline de competência e Ocorrência
 * projetada, que carrega o flag herdado da regra).
 */
export function isIgnoredTransaction(isIgnored: number | boolean | null | undefined): boolean {
    return isIgnored === 1 || isIgnored === true;
}
